import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ModalOverlay } from "@/components/ui/modal-overlay";
import { DateTimeField } from "@/components/ui/datetime-field";
import { toast } from "@/components/ui/notify";
import { useUpdatePlannedTimes } from "@/features/production/hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";

interface Props {
  lineId: string;
  runId: string;
  open: boolean;
  initialPlannedStartAt: string | null;
  initialExpectedEnd: string | null;
  onClose: () => void;
}

export function EditPlannedTimesDialog({
  lineId,
  runId,
  open,
  initialPlannedStartAt,
  initialExpectedEnd,
  onClose,
}: Props) {
  const [plannedStartAt, setPlannedStartAt] = useState<string | null>(initialPlannedStartAt);
  const [expectedEnd, setExpectedEnd] = useState<string | null>(initialExpectedEnd);
  const update = useUpdatePlannedTimes(lineId, runId);

  if (!open) return null;

  const crossFieldError =
    Boolean(plannedStartAt && expectedEnd) &&
    new Date(expectedEnd as string) <= new Date(plannedStartAt as string);
  const dirty = plannedStartAt !== initialPlannedStartAt || expectedEnd !== initialExpectedEnd;

  async function onSave() {
    if (!plannedStartAt && !expectedEnd) {
      toast.error("Informe ao menos um dos dois campos.");
      return;
    }
    if (crossFieldError) {
      toast.error("O fim planejado deve ser posterior ao início planejado.");
      return;
    }
    try {
      await update.mutateAsync({ plannedStartAt, expectedEnd });
      toast.success("Plano atualizado.");
      onClose();
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao atualizar plano.", { description: msg });
    }
  }

  return (
    <ModalOverlay
      onClose={onClose}
      title="Editar plano da produção"
      className="max-w-md"
      confirmDismiss={dirty}
    >
      <CardHeader>
        <CardTitle id="edit-planned-times-title">Editar plano da produção</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <DateTimeField
            id="edit-planned-start"
            label="Início planejado"
            value={plannedStartAt}
            onChange={setPlannedStartAt}
          />
          <DateTimeField
            id="edit-expected-end"
            label="Fim planejado"
            value={expectedEnd}
            onChange={setExpectedEnd}
            invalid={crossFieldError}
          />
          {crossFieldError ? (
            <p className="text-xs text-destructive" role="alert">
              O fim planejado deve ser posterior ao início planejado.
            </p>
          ) : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={() => void onSave()} disabled={update.isPending || crossFieldError}>
              {update.isPending ? "Salvando…" : "Salvar"}
            </Button>
          </div>
        </div>
      </CardContent>
    </ModalOverlay>
  );
}
